import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { map, Observable, Subscription } from 'rxjs';
import { GetProjectIdService } from '@/app/services/getProjectId.service';
import { NgxCurrencyDirective } from 'ngx-currency';
import { FormsModule } from '@angular/forms';
import { ApprovalStatusViewComponent } from '@/app/components/global/approval-status-view/approval-status-view.component';
import { PROJECT_STAGE } from '@/app/enums/project_stages.enum';
import { ReloadPlanDataService } from '@/app/services/shared/reload-plan-data.service';
import { SendForApprovalComponent } from '@/app/components/global/dialog-boxes/send-for-approval/send-for-approval.component';
import { NgToastService } from 'ng-angular-popup';
import { MatDialog } from '@angular/material/dialog';

interface ApprovalDetail {
  feedback: string | null;
  status: string;
  userName: string;
  email:string;
  userTitle: string | null;
  parentStatus: string;
}

@Component({
  selector: 'app-stage2viewmode',
  standalone: true,
  imports: [
    CommonModule,
    NgxCurrencyDirective,
    FormsModule,
    ApprovalStatusViewComponent,
  ],
  templateUrl: './stage2viewmode.component.html',
  styleUrl: './stage2viewmode.component.css',
})
export class Stage2viewmodeComponent implements OnInit {
  country: any;
  @Output() projectPlanEmitter: EventEmitter<any> = new EventEmitter<any>();

  nrmItems!: any; // This will hold the observable
  riskDescriptions$!: Observable<any>; // This will hold the observable
  projectId: any;

  total_work_packages = 0;
  total_cost = 0;
  number_of_risks = 0;

  average_risks = 0;
  moderate_risks = 0;
  high_risks = 0;
  progress: number = 0;
  isStageTwoCompleted: boolean = false;

  currencySymbol: any;
  private subscription: Subscription = new Subscription();
  approvalDetails: ApprovalDetail[] = [];
  @Output() editStage2 = new EventEmitter<boolean>();

  PROJECT_STAGE = PROJECT_STAGE;
  constructor(
    private router: Router,
    private projectsService: ProjectsService,
    private getProjectIdService: GetProjectIdService,
    private reloadPlanDataService: ReloadPlanDataService,
    private sanitizer: DomSanitizer, // Inject DomSanitizer // private countryService : CountryService
    private toast: NgToastService,
    private dialog: MatDialog
  ) {}
  ngOnInit(): void {
    this.getProjectIdService.projectId$.subscribe((id) => {
      if (!id) {
        this.projectId = this.getProjectIdService.getProjectId();
      } else {
        this.projectId = id;
      }
      this.nrmItems = this.getResponsibilityData(); // Assign the observable directly
      this.getRiskRegisterData(); // Assign and process the data in getRiskRegisterData
      this.getStageTwoApproversDetails();
      this.checkStageTwoApproval();
    });
    this.reloadPlanDataService.reloadPlanData$.subscribe((reload) => {
      if (reload) {
        this.nrmItems = this.getResponsibilityData(); // Assign the observable directly
        this.getRiskRegisterData(); // Assign and process the data in getRiskRegisterData
        this.getStageTwoApproversDetails();
        this.checkStageTwoApproval();
      }
    });
  }

  getResponsibilityData(): any {
    return this.projectsService.getResponsibilityMatrix().subscribe({
      next: (data: any) => {
        this.country = data?.data?.project_work_package[0]?.project?.country;
        this.nrmItems = data.data.project_work_package;
        this.progress =
          data?.data?.project_work_package[0]?.project?.stage_two_completion_percentage;

        this.total_work_packages = 0;
        this.total_cost = 0; // Reset before calculating

        this.nrmItems.forEach((work_package: any) => {
          this.total_work_packages++;
          // Parse cost_estimate as float and accumulate it to total_cost
          this.total_cost += parseFloat(work_package.cost_estimate) || 0;
        });

        // Ensure total_cost is stored as a float with two decimal places (optional)
        this.total_cost = parseFloat(this.total_cost.toFixed(2));
        this.projectPlanEmitter.emit({
          type: 'responsibilityMatrix',
          payload: data?.data,
        });
      },
      error: (error) => {
        console.error('Error fetching projects:', error);
      },
    });
  }

  getRiskRegisterData(): void {
    this.riskDescriptions$ = this.projectsService.getRiskRegister().pipe(
      map((response: any) =>
        response.data.map((risk: any) => ({
          ...risk,
          description: this.stripHtmlTags(risk.description),
          mitigation_strategy: this.stripHtmlTags(risk.mitigation_strategy), // Process description to plain text
        }))
      )
    );

    this.riskDescriptions$.subscribe({
      next: (risks: any[]) => {
        // let total_risk_rating = 0 ;

        risks.forEach((risk: any) => {
          if (
            risk.risk_rating &&
            risk.risk_rating > 0 &&
            risk.risk_rating < 11
          ) {
            this.average_risks++;
          } else if (
            risk.risk_rating &&
            risk.risk_rating > 10 &&
            risk.risk_rating < 16
          ) {
            this.moderate_risks++;
          } else if (
            risk.risk_rating &&
            risk.risk_rating > 15 &&
            risk.risk_rating < 26
          ) {
            this.high_risks++;
          }

          this.number_of_risks++;
          // total_risk_rating += risk.risk_rating || 0;
          // if(total_risk_rating && this.number_of_risks){
          //   this.average_risks = total_risk_rating / this.number_of_risks
          // }
        });
        this.projectPlanEmitter.emit({
          type: 'riskRegister',
          payload: risks,
        });
      },
      error: (error) => {
        console.error('Error fetching risk register:', error);
      },
    });
  }

  stripHtmlTags(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  navigateTo() {
    this.router.navigate(['/add-stage-2']);
  }

  handleProjectStage2Approval() {
    // if (!this.isStageTwoCompleted) {
    //   this.toast.warning('Project Plan is incomplete!', 'WARNING', 4000);
    //   return
    // }

    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      this.toast.danger('Project id not found', 'ERROR');
      return;
    }
    const dialogRef = this.dialog.open(SendForApprovalComponent, {
      // Sending props
      width:'80vw',
      data: {
        progress: this.progress,
        stage: PROJECT_STAGE?.STAGE_2,
        projectId: projectId,
        approvalResponses: this.approvalDetails,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.toast.success('Approval sent successfully!', 'SUCCESS', 4000);
        this.checkStageTwoApproval();
      }
    });
  }

  getStageTwoApproversDetails(): Promise<void> {
    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      return;
    }

    this.projectsService
      .fetchCurrentStageApprovalPercentage(projectId, 2)
      .subscribe({
        next: (response) => {
          if (response.meta.success) {
            this.isStageTwoCompleted = response.data == 100 ? true : false;
            console.log('this.isStageTwoCompleted', this.isStageTwoCompleted);
          }
        },
        error: (error) => {
          console.error('Error while fetching project percentage:', error);
        },
      });
  }

  // // this fetches approvers data not approval
  async checkStageTwoApproval(): Promise<void> {
    /**
     * send request to check if approval for stage 1 exists.
     * show component sendforapproval/project-stage-approval based on the result
     */
    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      return;
    }

    const data = {
      project_id: projectId,
      stage: PROJECT_STAGE?.STAGE_2,
    };

    try {
      const temp = await this.projectsService
        .getCurrentStageApproval(data)
        .toPromise();

      if (temp.data.length > 0) {
        this.approvalDetails = temp?.data?.map((item: any) => ({
          feedback: item.feedback,
          status: item.status,
          userName: item.user.name,
          userTitle: item.user.title,
          email:item.user.email,
          parentStatus: item?.parent_status
        }));
      }
    } catch (error) {
      console.log(error);
    }
  }

  handleEdit() {
    this.editStage2.emit();
  }
}
